// contentHelpers.js
// Functions related to editing content within the editor (triggered by the ToolBar)
import { Slice, Fragment } from 'prosemirror-model';

/**
 * Get current classes from the active node (paragraph, heading, listItem, blockquote, or codeBlock)
 * @param {Editor} editor - TipTap editor instance
 * @returns {string} - Current class string
 */
export function getClasses(editor) {
  if (!editor) return '';
  
  // Check if we're in a blockquote
  if (editor.isActive('blockquote')) {
    const attrs = editor.getAttributes('blockquote') || {};
    return attrs.class || '';
  }
  // Check if we're in a code block (pre)
  if (editor.isActive('codeBlock')) {
    const attrs = editor.getAttributes('codeBlock') || {};
    return attrs.class || '';
  }
  // Check if we're in a list
  if (editor.isActive('taskList')) {
    const attrs = editor.getAttributes('taskItem') || {};
    return attrs.class || '';
  }
  if (editor.isActive('bulletList') || editor.isActive('orderedList')) {
    const attrs = editor.getAttributes('listItem') || {};
    return attrs.class || '';
  }
  // Check if we're in a heading
  if (editor.isActive('heading')) {
    const attrs = editor.getAttributes('heading') || {};
    return attrs.class || '';
  }
  // Default to paragraph
  const attrs = editor.getAttributes('paragraph') || {};
  return attrs.class || '';
}

/**
 * Update classes on the active node (removes old in* and al* classes and adds new one)
 * @param {Editor} editor - TipTap editor instance
 * @param {string} newClass - New class to add (e.g., 'in2', 'alC')
 */
export function updateClasses(editor, newClass) {
  if (!editor) return;
  
  const currentClasses = getClasses(editor);
  // Remove all in* and al* classes
  const cleaned = currentClasses.replace(/\b(in[1-6]|al[LCRJ])\b/g, '').trim();
  // Add new class if provided
  const updated = newClass ? `${cleaned} ${newClass}`.trim() : cleaned;
  
  // Determine which node type to update
  let nodeType = 'paragraph';
  if (editor.isActive('blockquote')) {
    nodeType = 'blockquote';
  } else if (editor.isActive('codeBlock')) {
    nodeType = 'codeBlock';
  } else if (editor.isActive('taskList')) {
    nodeType = 'taskItem';
  } else if (editor.isActive('bulletList') || editor.isActive('orderedList')) {
    nodeType = 'listItem';
  } else if (editor.isActive('heading')) {
    nodeType = 'heading';
  }
  
  editor.chain().focus().updateAttributes(nodeType, {
    class: updated || null
  }).run();
}

/**
 * Check if a class is active on the current node
 * @param {Editor} editor - TipTap editor instance
 * @param {string} className - Class name to check
 * @returns {boolean}
 */
export function hasClass(editor, className) {
  return getClasses(editor).includes(className);
}

/**
 * Get current indent level (0-6)
 * @param {Editor} editor - TipTap editor instance
 * @returns {number}
 */
export function getCurrentIndent(editor) {
  if (!editor) return 0;
  const classes = getClasses(editor);
  const match = classes.match(/\bin([1-6])\b/);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * Set indent level (0-6) on the active node
 * @param {Editor} editor - TipTap editor instance
 * @param {number} level - Indent level (0-6)
 */
export function setIndent(editor, level) {
  if (!editor) return;
  
  const classes = getClasses(editor);
  const cleaned = classes.replace(/\bin[1-6]\b/g, '').trim();
  const updated = level > 0 ? `${cleaned} in${level}`.trim() : cleaned;
  
  // Determine which node type to update
  let nodeType = 'paragraph';
  if (editor.isActive('blockquote')) {
    nodeType = 'blockquote';
  } else if (editor.isActive('codeBlock')) {
    nodeType = 'codeBlock';
  } else if (editor.isActive('taskList')) {
    nodeType = 'taskItem';
  } else if (editor.isActive('bulletList') || editor.isActive('orderedList')) {
    nodeType = 'listItem';
  } else if (editor.isActive('heading')) {
    nodeType = 'heading';
  }
  
  editor.chain().focus().updateAttributes(nodeType, {
    class: updated || null
  }).run();
}

/**
 * Returns the current indent level for a paragraph node (0 if none)
 * @deprecated Use getCurrentIndent instead
 * @param {Editor} editor - TipTap editor instance
 * @returns {number}
 */
export function getCurrentIndentLevel(editor) {
  return getCurrentIndent(editor);
}

/**
 * Sets the indent level for a paragraph node
 * @deprecated Use setIndent instead
 * @param {Editor} editor - TipTap editor instance
 * @param {number} level - Indent level (0-6)
 */
export function setIndentLevel(editor, level) {
  setIndent(editor, level);
}

export function cleanNode(node) {
  if (node.isText) return node;
  const cleanedAttrs = node.attrs ? { ...node.attrs } : {};
  delete cleanedAttrs.chunkId;
  delete cleanedAttrs.chunkHash;
  delete cleanedAttrs.chunkPrev;
  delete cleanedAttrs.chunkNext;
  delete cleanedAttrs.chunk;
  let cleanedContent = node.content;
  if (node.content && node.content.size > 0) {
    const cleanedChildren = [];
    node.content.forEach((child) => {
      cleanedChildren.push(cleanNode(child));
    });
    cleanedContent = Fragment.from(cleanedChildren);
  }
  return node.type.create(cleanedAttrs, cleanedContent, node.marks);
}

export function cleanSlice(slice) {
  const cleanedNodes = [];
  slice.content.forEach((node) => {
    cleanedNodes.push(cleanNode(node));
  });
  const cleanedContent = Fragment.from(cleanedNodes);
  return new Slice(cleanedContent, slice.openStart, slice.openEnd);
}
